import React from "react";
import {
  Typography,
  Divider,
  Box,
  Paper,
  Avatar,
  Container,
  useTheme,
  useMediaQuery,
  CardMedia,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";

export default function Article9(props: any) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Team colors
  const metsColor = '#002D72';
  const raysColor = '#092C5C';
  const dodgersColor = '#005A9C';
  
  // Velocity data
  const velocityData = [
    { player_name: "Glasnow, Tyler", velocity: 95.8, effective_speed: 98.28, extension_boost: 2.48 },
    { player_name: "Díaz, Alexis", velocity: 93.5, effective_speed: 95.72, extension_boost: 2.22 },
    { player_name: "Gervase, Paul", velocity: 93.8, effective_speed: 95.99, extension_boost: 2.19 },
    { player_name: "Gilbert, Logan", velocity: 95.5, effective_speed: 97.66, extension_boost: 2.16 },
    { player_name: "Misiorowski, Jacob", velocity: 99.3, effective_speed: 101.45, extension_boost: 2.15 },
  ];

  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography
          variant="h1"
          sx={{
            fontWeight: 800,
            mb: 2,
            fontSize: props.isDesktop ? "2.9rem" : "1.5rem",
            lineHeight: 1.2,
            color: "text.primary"
          }}
        >
          Extension and Perseverance: How 6'10" D3 Walk-On Paul Gervase Pitched His Way to the Los Angeles Dodgers
        </Typography>
        
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
            color: "text.secondary",
            fontSize: isMobile ? "1rem" : "1.1rem"
          }}
        >
          By Duke Hill • September 7th, 2025
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 4 }} />
      {/* Embedded YouTube Video */}
      <Box sx={{ mb: 4, borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>
        <CardMedia
          component="iframe"
          src="https://www.youtube.com/embed/Si54qvbgCRQ"
          title="Interview with Paul Gervase"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sx={{
            width: '100%',
            height: isMobile ? '250px' : '500px',
            border: 'none'
          }}
        />
      </Box>

      {/* Video Blurb */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 4, 
          bgcolor: 'grey.50',
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          borderRadius: 1,
          fontStyle: 'italic'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          📹 All quotes in this article are excerpted from this exclusive interview with Paul Gervase, 
          conducted September 2nd, 2025. Watch the full conversation to hear directly from the Dodgers' 
          rising pitching prospect about his unconventional journey to the majors.
        </Typography>
      </Paper>

        <div style={{ textAlign: "center", margin: "20px 0", maxWidth: "100%" }}>
        <a href="/player/801434">
          <img src={require('../images/Gervase.png')} alt="loading..." style={{ maxWidth: "90%", border: "#293241 solid 2px" }} /></a>
      </div>

      {/* Opening Quote */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          my: 5, 
          bgcolor: 'primary.main', 
          color: 'white',
          borderRadius: 2,
          borderLeft: `4px solid red`
        }}
      >
        <Typography variant="h6" sx={{ fontStyle: 'italic', mb: 0 }}>
          "I just throw the ball and it happens."
        </Typography>
      </Paper>

      

      {/* Article Content - Remainder of the article continues unchanged */}
      <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          It's the kind of simple, self-effacing quote that perfectly captures Paul Gervase's approach to pitching - one that belies the incredible complexity of his journey to the majors. Standing at a towering 6'10", the Los Angeles Dodgers' newest relief prospect doesn't fit any conventional mold. His path from Division III walk-on 
          to big league mound has been anything but straight, taking him through multiple junior college transfers, working at different Olive Garden locations to pay for the journey, playing in the Appalachian and Cape Cod leagues, closing games for LSU, and getting drafted and traded before finally reaching the show.
        </Typography>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
          What makes Gervase's story remarkable isn't just his unconventional route, but how his unique profile - that massive frame generating elite extension and deceptive velocity - made him exactly the kind of pitcher the Dodgers' analytics department treasures. His big league debut earlier this season represented the culmination of a journey that proves sometimes the best prospects are those from unexpected places most determined to battle and grind their way to the top.
        </Typography>

        {/* Section Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, fontSize: "2.2rem", mb: 1, color: 'primary.main' }}>
            The Grind: An Unconventional Ascent
          </Typography>
          <Divider sx={{ mb: 3 }} />
        </Box>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Gervase's baseball journey reads like a roadmap of the sport's forgotten corners. It began as a Division III walk-on at Pfeiffer University, where his frame held more potential than results. From there came multiple junior college transfers, a summer in the Appalachian League where he balanced baseball with shifts at Olive Garden, and finally, his breakthrough at LSU.
        </Typography>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          What kept him going when the odds seemed longest? A simple family mantra and pure love for the game that sustained him through countless challenges.
        </Typography>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "Yeah, it wasn't easy… My dad has always told me my whole life, what you're doing is better than working a day job. And I'm like, all right, well, let's ride this thing till the wheels fall off."
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          That mindset carried him through the non-traditional stops, including working multiple restaurant jobs to keep his baseball dream alive. The breakthrough didn't come from a radar gun reading, but through gradual progress and small victories that built his confidence inning by inning.
        </Typography>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "It started to click when I got to Pitt Community College and started to kind of believe in myself. And then once LSU reached out and a couple other schools, I was like, all right, like, I got a shot with this. And then that was a huge help to my confidence."
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          The real validation came not just from LSU's interest, but from the results he began producing against top competition:
        </Typography>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "And then just getting to LSU and starting to dominate and throw up zeros, I'm like, all right, I can compete at the highest level. So it was. I would say it definitely wasn't easy, but I would say just faith and determination and family, they all just kind of kept me going for sure."
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
          The first true sign he'd made it wasn't just a statistical achievement, but a financial milestone that made all those years of grind worthwhile: using his signing bonus from the New York Mets to pay off his student loans, finally turning his baseball dream into tangible success.
        </Typography>

        {/* Section Header */}
        <Box sx={{ mb: 4, mt: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'primary.main', fontSize: "2.0rem", }}>
            The Mindset: Embracing the Grind, Ignoring the Noise
          </Typography>
          <Divider sx={{ mb: 3 }} />
        </Box>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          That hard-won perspective, forged on baseball's backroads, has given Gervase a stoic mentality that serves him well in high-pressure situations. When asked about maintaining intensity while competing for a playoff bullpen spot down the stretch, his answer revealed a player who's mentally as prepared as he is physically.
        </Typography>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "I treat every baseball game the same. Like whether it was my major league debut, which obviously was a huge deal, or another game here in AAA. I think if you get too high or too low, that's where you start messing with the mental stuff. So if you keep it every day the same. Yeah, obviously you want to be on the playoff roster, you want to play in the major leagues, but there's nothing I can do about that right now. So every outing, just treat it like every other game and just go out there with that emphasis of throwing strike one and then just getting after guys. And for sure I'm locked in and I'm on. I feel like I'm gonna win most of the time."
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
          This isn't just recycled coach-speak - it's the operational philosophy of a pitcher who's learned to control only what he can control. His laser focus on the micro aspect of "throwing strike one" connects his outings in the Appalachian League to his high-leverage appearances for Oklahoma City. It's the same approach that kept him grounded moments after learning he'd been traded at the highest level of the sport.
        </Typography>

        {/* Section Header */}
        <Box sx={{ mb: 4, mt: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'primary.main', fontSize: "2.0rem", }}>
            The Three-Team Deadline Trade: A Whirlwind 24 Hours
          </Typography>
          <Divider sx={{ mb: 3 }} />
        </Box>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Gervase's 2025 campaign with the Tampa Bay Rays showcased exactly why he became such a valuable trade commodity. He posted one of the most dominant strikeout rates in all of Triple-A, establishing himself as the next potential relief ace from an organization famous for developing them. His breakthrough coincidentally aligned with the Rays' decision to become sellers at the trade deadline, setting the stage for a big move.
        </Typography>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          The trade that changed everything involved three teams - Rays, Dodgers, and Reds - in a complex deal that demonstrated how much Los Angeles valued Gervase's unique profile:
        </Typography>

        <Box component="ul" sx={{ pl: 4, mb: 3 }}>
          <li><Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 1 }}><strong>Dodgers received:</strong> LHP Adam Serwinowski (from Reds), RHP Paul Gervase & C Ben Rortvedt (from Rays)</Typography></li>
          <li><Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 1 }}><strong>Rays received:</strong> C Hunter Feduccia (from Dodgers) & RHP Brian Van Belle (from Reds)</Typography></li>
          <li><Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 1 }}><strong>Reds received:</strong> RHP Zack Littell (from Rays)</Typography></li>
        </Box>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          For Gervase, the news arrived amidst a whirlwind 24 hours that perfectly captured the chaos of deadline season. He'd just been called up to the majors and was settling into the visitor's clubhouse at Yankee Stadium when his phone rang with life-changing news.
        </Typography>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "That was a crazy day because the night before I was in Gwinnett in AAA and the manager called me in the hotel room, 'hey, you're going up.' All right, let's go. Going to Yankee Stadium... I fly from Atlanta to New York, get there at like four, stretch, throw. We play an extra inning game. I don't pitch. And then after the game, we found out Littell got traded. So we're all like, 'oh, see ya, man,' like, saying our goodbyes. Get on the bus to go back to the hotel. I'm chilling. I get a call from a random number... It's Erik Neander, general manager for the Rays. Like, 'we just traded you to the Dodgers.' I was like, 'huh?'"
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
          The move exemplified the Dodgers' strategy of identifying specific, elite traits - in this case, Gervase's unparalleled extension (4th in all of MLB) - and aggressively pursuing them, no matter where they might be found.
        </Typography>

          <div style={{ textAlign: "center", margin: "20px 0", maxWidth: "100%" }}>
          <img src={require('../images/GervaseExtension.png')} alt="loading..." style={{ maxWidth: "100%", border: "#293241 solid 2px" }} />
          <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 0.5,
                    fontStyle: 'italic',
                    fontSize: '0.75rem'
                  }}
                >
                  Source: <a href="https://baseballsavant.mlb.com/statcast_search?hfPT=&hfAB=&hfGT=R%7C&hfPR=&hfZ=&hfStadium=&hfBBL=&hfNewZones=&hfPull=&hfC=&hfSea=2025%7C&hfSit=&player_type=pitcher&hfOuts=&hfOpponent=&pitcher_throws=&batter_stands=&hfSA=&game_date_gt=&game_date_lt=&hfMo=&hfTeam=&home_road=&hfRO=&position=&hfInfield=&hfOutfield=&hfInn=&hfBBT=&hfFlag=&metric_1=&group_by=name&min_pitches=0&min_results=0&min_pas=0&sort_col=release_extension&player_event_sort=api_p_release_speed&sort_order=desc&chk_stats_release_extension=on#results">Baseball Savant</a>
                </Typography>
      </div>
        

        {/* Section Header */}
        <Box sx={{ mb: 4, mt: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'primary.main', fontSize: "2.0rem", }}>
            The Dodger Debut: Inheriting the Aftermath of an Ohtani Start
          </Typography>
          <Divider sx={{ mb: 3 }} />
        </Box>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Gervase's Dodgers debut arrived under circumstances as unique as his journey. On August 20, 2025, he got the call to Coors Field as the Dodgers faced the Colorado Rockies in a game featuring Shohei Ohtani's first start at the hitter-friendly venue.
        </Typography>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          The game didn't unfold as scripted for Shohei. Ohtani struggled uncharacteristically, tying his career-high by allowing nine hits and surrendering five runs over four innings. Adding injury to insult, he took a 93.7 mph comebacker off his upper right leg in the fourth inning, though he managed to remain in the game.
        </Typography>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          When Ohtani's night ended after four challenging frames, the Dodgers first turned to reliever Ednel Henriquez, who worked a scoreless fifth to stabilize the game. Then, for the sixth inning, Manager Dave Roberts called upon his newest weapon: Paul Gervase.
        </Typography>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          The moment carried profound symbolism. This wasn't just about relieving an ace; it was about entrusting the middle innings to keep the team within striking distance. It represented the Dodgers' faith in his abilities not as a mop-up man, but as a genuine weapon, even on baseball's most challenging pitching stage.
        </Typography>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Yet typical of his grounded mentality, Gervase's focus wasn't on the glamour of the moment but on executing his job. Reflecting on the debut, his mind went not to following a legend, but to the specifics of his performance.
        </Typography>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "I didn't really think of it as, like, a big thing. I guess it was my first outing with the Dodgers, but I just thought of it as, this team needs a win, and thankfully, they think I can help them win. So let's go out there and try to put up a couple of zeros... I walked the leadoff guy, which was pretty frustrating. But other than that, I thought I made some pretty good pitches."
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4 }}>
          Fretting over a leadoff walk while barely mentioning he'd just shared a big-league mound with Shohei Ohtani perfectly captures his process-oriented mindset. Despite the difficult Coors Field conditions, Gervase showed the poise that defined his career. Over two innings, he struck out two batters, and while that leadoff walk clearly stuck with him, his overall performance demonstrated a veteran's calm despite the circumstances.
        </Typography>

        {/* Section Header */}
        <Box sx={{ mb: 4, mt: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'primary.main', fontSize: "2.0rem", }}>
            The Arsenal: Deconstructing a Unique Weapon
          </Typography>
          <Divider sx={{ mb: 3 }} />
        </Box>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          At LSU, despite throwing what he estimates was a staggering percentage of fastballs, hitters consistently struggled to make solid contact. Puzzled by his success compared to harder-throwing teammates, he sought answers from his coaches.
        </Typography>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "I was up there throwing, like, 96% fastballs. And they would get sometimes I would just randomly change my arm slot just because, I mean, they knew what was coming and then they weren't hitting it. So I asked my coach one day. I was like, like, we got guys that are harder than me on this team, and their fastball gets hit, and they actually have a breaking ball. Like, why is mine not getting hit? He's just like, 'well, your fastball is very unique.' And, like, he told me about the characteristics of it, and I was like, 'oh, sick. All right. Yeah, well, let's keep doing that.' I mean, it's never something I've really tried to have or, like, that we've tried to develop… I just throw the ball and it happens."
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          The secret weapon is his incredible extension - Gervase releases the ball a full 7.5 feet closer to home plate than the location of the rubber, effectively cutting the time the ball is in the air. Our models at Prospect Savant have long flagged his unique metrics, and the data remains staggering. His four-seam fastball ranks in the top three in all of Major League Baseball for perceived velocity boost created by extension, placing him in the company of teammate Tyler Glasnow and Alexis Diaz, who was recently designated for assignment by the Dodgers.
        </Typography>

                {/* Velocity Comparison Table */}
        <Box sx={{ my: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
            Elite Extension Leaders: Perceived Velocity Boost
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', fontStyle: 'italic', color: 'text.secondary' }}>
            Top MLB 4 seam fastballs by extension/location-induced perceived velocity increase (mph)
          </Typography>
          
          <TableContainer component={Paper} elevation={2} sx={{ mb: 4 }}>
            <Table sx={{ minWidth: 650 }} aria-label="velocity comparison table">
              <TableHead sx={{ bgcolor: 'primary.main' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Pitcher</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Velocity (mph)</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Effective Speed (mph)</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Extension Boost (mph)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {velocityData.map((row, index) => (
                  <TableRow
                    key={row.player_name}
                    sx={{ 
                      '&:last-child td, &:last-child th': { border: 0 },
                      bgcolor: row.player_name === "Gervase, Paul" ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                    }}
                  >
                    <TableCell component="th" scope="row" sx={{ fontWeight: row.player_name === "Gervase, Paul" ? 600 : 'normal' }}>
                      {row.player_name}
                    </TableCell>
                    <TableCell align="right">{row.velocity}</TableCell>
                    <TableCell align="right">{row.effective_speed}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>+{row.extension_boost}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          His pitching philosophy remains as straightforward as his fastball is complex.
        </Typography>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "Everything I do is all about strike one. For strike one, the fastball, slider, cutter. Either way, throw strike one. When I throw strike one, I'm a lot more successful than when I throw ball one."
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          He's wisely abandoned a brief sweeper experiment that spiked his walk rate, instead perfecting a devastatingly effective three-pitch combination.
        </Typography>

        <div style={{ textAlign: "center", margin: "20px 0", maxWidth: "100%" }}>
        <a href="/player/801434">
          <img src={require('../images/GervaseMovement.png')} alt="loading..." style={{ maxWidth: props.isDesktop? "50%" : "90%", border: "#293241 solid 2px" }} /></a>
      </div>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "As you saw, the sweeper I threw in April, that was it. That was the only time I threw it... It was messing up my fastball shape, and my fastball was not in the strike zone enough... We just kind of went back to a more normal slider and started dominating again for sure. You will not see that come back."
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          His two-strike approach is equally direct and effective:
        </Typography>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "I'm trying to throw everything down the middle until two strikes, and then two strikes I'm gonna try to elevate it or get my slider down."
          </Typography>
        </Paper>

        {/* Section Header */}
        <Box sx={{ mb: 4, mt: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'primary.main', fontSize: "2.0rem", }}>
            The Education: A Three Lab Formula
          </Typography>
          <Divider sx={{ mb: 3 }} />
        </Box>

                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Few pitchers have received tutoring from as many renowned pitching labs as Gervase. He's absorbed bits and pieces from each system to build his complete approach:
        </Typography>


        {/* Mets Quote */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: metsColor,
            color: 'white',
            borderRadius: 1,
            borderLeft: `4px solid #FF5910` // Mets orange accent
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: "bold", mb: 0, lineHeight: 1.6, color: "#FF5910" }}>
            New York Mets
          </Typography>
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "I think what I learned the most with the Mets was just believing in myself and trusting my stuff... They were just saying, 'hey, you have good stuff. Go out there and throw with conviction and dominate.'"
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          
        </Typography>

        {/* Rays Quote */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: raysColor,
            color: 'white',
            borderRadius: 1,
            borderLeft: `4px solid #8FBCE6` // Rays light blue accent
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: "bold", mb: 0, lineHeight: 1.6, color: "#8FBCE6" }}>
            Tampa Bay Rays
          </Typography>
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "Like you said with the Rays, it was like, if you get in the strike zone, you're a major leaguer... if you want to play in the major leagues for the Rays, you got to stand out and you got to compete with these guys. And it was so fun." He described the Rays' system as a friendly but intense competition among command artists.
          </Typography>
        </Paper>


        {/* Dodgers Quote */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: dodgersColor,
            color: 'white',
            borderRadius: 1,
            borderLeft: `4px solid #EF3E42` // Dodgers red accent
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: "bold", mb: 0, lineHeight: 1.6 }}>
            Los Angeles Dodgers
          </Typography>
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            Pure empowerment. Upon arrival, he was told, "We traded for you because we like how good you are. Now do what you do."
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          His synthesis proves potent: the Rays' fanatical focus on strike-throwing combined with the Dodgers' mandate to unleash his best stuff. The results have included a noticeable performance jump and velocity increase since the trade, something he discovered through our conversation.
        </Typography>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "I have no idea. I didn't even know that I have been up a tick. That's good to know. I appreciate that."
          </Typography>
        </Paper>

        {/* Section Header */}
        <Box sx={{ mb: 4, mt: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: 'primary.main', fontSize: "2.0rem", }}>
            The Future: Eyes on the Playoffs
          </Typography>
          <Divider sx={{ mb: 3 }} />
        </Box>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          Gervase has already achieved his lifelong dream with his MLB debut at Steinbrenner Field, which he called "the best day of my life." But his goals have quickly evolved from simply reaching the show to winning on baseball's biggest stages.
        </Typography>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "(My next goal is to) Pitch in the playoffs and help this team win. Because, well, yeah, (the debut) was awesome. Now I want to contribute for the Dodgers. I want to help teams win... This is a team game. It's not just about doing everything for myself."
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 3 }}>
          For someone who once wondered if his baseball dream would survive another season, the possibility of a World Series ring feels almost surreal. He credits his faith and support system for the resilience that's fueled his rapid ascent.
        </Typography>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            my: 4, 
            bgcolor: 'grey.100',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            borderRadius: 1
          }}
        >
          <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 0, lineHeight: 1.6 }}>
            "It's all God, dude. That is not... it's not Paul Gervase. God's had a plan for me. I'm so grateful that I'm here and didn't give up and people stuck by me, and that would mean a lot, for sure."
          </Typography>
        </Paper>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 6 }}>
          Paul Gervase's story transcends the typical feel-good narrative. It's a blueprint for how under-the-radar talent can be identified, developed, and unleashed. He embodies the modern pitching prospect: a blend of unteachable physical traits, refined simplicity in approach, and wisdom absorbed from the game's brightest minds. As the Dodgers prepare for October, they've added a weapon forged not on the travel ball circuit, but in the grind, refined by data, and acquired through shrewd understanding of what truly makes a pitcher dominate.
        </Typography>

        {/* Author Bio */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mt: 8, 
          p: 3, 
          bgcolor: 'grey.50', 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <Avatar sx={{ mr: 2, width: 56, height: 56, bgcolor: 'primary.main' }}>DH</Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Duke Hill</Typography>
            <Typography variant="body2" color="text.secondary">
              Creator and Editor in Chief of Prospect Savant
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}